import React, { useContext, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SocketContext } from "../context/SocketProvider";
import { GAME_STAGES, DEFAULT_ROOM } from "../constants/gameStages";
import ReturnHomeButton from "./ReturnHomeButton";
import { FaChevronRight, FaChevronDown } from "react-icons/fa6";
import { useLanguage } from "../context/LanguageContext";


const SettingsModal = ({
  isOpen,
  onClose,
  imageCount,
  toggleImageCount,
  canSwapRoles,
}) => {
  const { gameStage, role, socket } = useContext(SocketContext);
  const { language, setLanguage, t } = useLanguage();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      // Don't close if clicking on ReturnHomeButton overlay
      if (event.target.closest('.return-home-overlay') || 
          event.target.closest('[role="dialog"]')) {
        return;
      }
      
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleModalClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleModalClickOutside);
    };
  }, [isOpen, onClose]);

  const handleLanguageSelect = (selectedLanguage) => {
    setLanguage(selectedLanguage === "en" ? "en" : "ja");
    setIsLanguageDropdownOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const handleRoleSwap = () => {
    if (canSwapRoles) {
      socket.emit("swap_roles", { room: DEFAULT_ROOM });
    }
  };

  // Dynamic styles based on role and game stage
  const color =
    gameStage === GAME_STAGES.RESULTS
      ? "yellow"
      : role === "describer"
      ? "pink"
      : "blue";

  const bgColor = `bg-${color}400`;
  const textColor = `text-${color}400`;
  const borderColor = `border-${color}400`;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 99999 }}>
      <div ref={modalRef} className="bg-white rounded-2xl overflow-hidden w-[522px] shadow-2xl">
        {/* Header */}
        <header
          className={`w-full h-16 ${bgColor} flex items-center justify-center`}
        >
          <h2 className="text-white text-[22px] font-bold text-center font-['Gabarito']">
            {t('settings.title', 'SETTINGS:')}
          </h2>
        </header>

        {/* Content */}
        <div className="p-10 space-y-8">
          {/* Language Setting */}
          <div className="flex items-center justify-between gap-4">
            <span className="text-black text-[26px] font-normal font-['Gabarito']">
              {t('settings.language', 'Language:')}
            </span>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleLanguageDropdown}
                className={`btn-${color}-sm-transparent relative w-[232px] h-[45px]`}
              >
                <span className="btn-text-pos-right-sm">
                  {language === "en" ? "EN" : "JP"}
                </span>
                <FaChevronDown className={`arrow-pos-right-sm transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className={`absolute top-full left-0 mt-1 bg-white w-[232px] border-[3px] ${borderColor} rounded-md shadow-lg z-[120] overflow-hidden`}>
                  <button
                    onClick={() => handleLanguageSelect("en")}
                    className={`w-full px-3 py-2 text-center font-bold font-['Gabarito'] transition-all hover:shadow-[inset_0_4px_0_0_rgba(0,0,0,0.25)] ${
                      language === "en" ? `${bgColor} text-white` : `${textColor} bg-white`
                    }`}
                  >
                    {language === "en" ? "ENGLISH" : "ENGLISH"}
                  </button>
                  <button
                    onClick={() => handleLanguageSelect("ja")}
                    className={`w-full px-3 py-2 text-center font-bold font-['Gabarito'] transition-all hover:shadow-[inset_0_4px_0_0_rgba(0,0,0,0.25)] ${
                      language === "ja" ? `${bgColor} text-white` : `${textColor} bg-white`
                    }`}
                  >
                    {language === "ja" ? "日本語" : "日本語"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Role Setting */}
          <div className="flex items-center justify-between gap-4">
            <span className={`text-black text-[26px] font-normal  ${language === "ja" ? 'w-[100px] h-[45px]' : 'w-[233px] h-[45px]'} font-['Gabarito']`}>
              {t('settings.role', 'Role:')}
            </span>
            <div
              className={`toggle-container ${borderColor} ${
                !canSwapRoles ? "opacity-50" : ""
              }`}
            >
              <button
                onClick={canSwapRoles ? handleRoleSwap : undefined}
                disabled={!canSwapRoles}
                className={`toggle-btn text-[21.35px] ${
                  role === "describer"
                    ? `${bgColor} text-white`
                    : `${textColor} bg-white`
                } ${!canSwapRoles ? "toggle-btn-disabled" : ""}`}
              >
                {t('role.describer', 'Describer')}
              </button>
              <button
                onClick={canSwapRoles ? handleRoleSwap : undefined}
                disabled={!canSwapRoles}
                className={`toggle-btn ${
                  role === "guesser"
                    ? `${bgColor} text-white`
                    : `${textColor} bg-white`
                } ${!canSwapRoles ? "toggle-btn-disabled" : ""}`}
              >
                {t('role.guesser', 'Guesser')}
              </button>
            </div>
          </div>

          {/* Number of Images Setting */}
          {/* <div className="flex items-center justify-center gap-4">
            <span className="text-black text-[26px] font-normal font-['Gabarito']">
              {t('settings.images', 'Number of Images:')}
            </span>
            <div
              className={`toggle-container ${borderColor} ${
                gameStage !== GAME_STAGES.READY ? "opacity-50" : ""
              }`}
            >
              <button
                onClick={
                  gameStage === GAME_STAGES.READY ? toggleImageCount : undefined
                }
                disabled={gameStage !== GAME_STAGES.READY}
                className={`toggle-btn ${
                  imageCount === 6
                    ? `${bgColor} text-white`
                    : `${textColor} bg-white`
                } ${
                  gameStage !== GAME_STAGES.READY ? "toggle-btn-disabled" : ""
                }`}
              >
                6
              </button>
              <button
                onClick={
                  gameStage === GAME_STAGES.READY ? toggleImageCount : undefined
                }
                disabled={gameStage !== GAME_STAGES.READY}
                className={`toggle-btn ${
                  imageCount === 8
                    ? `${bgColor} text-white`
                    : `${textColor} bg-white`
                } ${
                  gameStage !== GAME_STAGES.READY ? "toggle-btn-disabled" : ""
                }`}
              >
                8
              </button>
            </div>
          </div> */}

          {/* Return to Home Button */}
          <div className="flex justify-center pt-8">
            <ReturnHomeButton
              text={t('settings.returnHome', 'Return to Home')}
              className={`btn-${color}-md-transparent`}
              onConfirm={onClose}
              showConfirmation={false}
            />
          </div>
        </div>

        {/* Done button */}
        <div className="h-32 bg-white relative">
          <button
            onClick={onClose}
            className={`btn-${color}-sm btn-bottom-right-sm`}
          >
            <span className="btn-text-pos-right-sm">{t('button.done', 'Done!')}</span>
            <FaChevronRight className="arrow-pos-right-sm" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SettingsModal;
